import React, { useState, useEffect } from 'react';
import type { SavedSignature, SignerProfile, SigningField, Snapshot } from '../types/fields';
import type { WorkflowStage } from '../constants/signing';
import { WORKFLOW_STAGE_LABELS } from '../constants/signing';
import { FieldPropertiesPanel } from './FieldPropertiesPanel';
import { SignaturesPanel } from './SignaturesPanel';
import type { TextFormatPatch } from '../utils/textFormatActions';
import { isTextBasedFieldType } from '../utils/fontMapping';

interface RightInspectorProps {
  selectedField: SigningField | null;
  fields: SigningField[];
  savedSignatures: SavedSignature[];
  signerProfile: SignerProfile;
  onProfileChange?: (patch: Partial<SignerProfile>) => void;
  documentName?: string;
  pageCount?: number;
  fileSize?: number;
  savedSignatureCount?: number;
  hasAcroFormFields?: boolean;
  memoryTip?: string;
  workflowStage?: WorkflowStage;
  onFieldUpdate: (id: string, updates: Partial<SigningField>) => void;
  onApplyTextFormat?: (patch: TextFormatPatch, targetIds: string[]) => void;
  bulkFieldIds?: string[];
  onBulkFieldIdsChange?: (ids: string[]) => void;
  onFieldDelete: (id: string) => void;
  onFieldSelect: (id: string | null) => void;
  onPlaceSavedSignature: (signature: SavedSignature) => void;
  onRenameSavedSignature: (id: string, label: string) => void;
  onDeleteSavedSignature: (id: string) => void;
  hasPdf?: boolean;
  isBusy?: boolean;
  onCreateDrawSignature?: () => void;
  onCreateTypeSignature?: () => void;
  onCreateUploadSignature?: () => void;

  // Snapshot feature (passed from parent) - now uses proper Snapshot type with optional per-doc metadata
  snapshots?: Snapshot[];
  onLoadSnapshot?: (snapshot: Snapshot) => void;
  onDeleteSnapshot?: (id: string) => void;
  onCompareSnapshot?: (id: string) => void;
  onRenameSnapshot?: (id: string, name: string) => void;
  /** ID of the snapshot currently shown as ghost layer (for visual affordance in list) */
  comparingSnapshotId?: string | null;

  // Drag reorder + lightweight groups (added for high-impact organization, native HTML5 drag, no new deps)
  onReorderFields?: (fromIndex: number, toIndex: number) => void;
  onReorderSnapshots?: (fromIndex: number, toIndex: number) => void;
  onSetSnapshotGroup?: (id: string, group?: string) => void;
  onExportTemplate?: () => void;
  onImportTemplate?: () => void;
  canExportTemplate?: boolean;
  /** Contract #2: single-call bulk clear (single undo entry via useSigningFields.clearFields). */
  onClearAllFields?: () => void;
}

const RightInspectorComponent: React.FC<RightInspectorProps> = ({
  selectedField,
  fields = [],
  savedSignatures,
  signerProfile,
  onProfileChange,
  documentName = '',
  pageCount = 0,
  fileSize = 0,
  savedSignatureCount = 0,
  hasAcroFormFields = false,
  memoryTip = '',
  workflowStage,
  onFieldUpdate,
  onApplyTextFormat,
  bulkFieldIds = [],
  onBulkFieldIdsChange,
  onFieldDelete,
  onFieldSelect,
  onPlaceSavedSignature,
  onRenameSavedSignature,
  onDeleteSavedSignature,
  hasPdf = false,
  isBusy = false,
  onCreateDrawSignature,
  onCreateTypeSignature,
  onCreateUploadSignature,
  snapshots = [],
  onLoadSnapshot,
  onDeleteSnapshot,
  onCompareSnapshot,
  onRenameSnapshot,
  comparingSnapshotId = null,
  onReorderFields,
  onReorderSnapshots,
  onSetSnapshotGroup,
  onExportTemplate,
  onImportTemplate,
  canExportTemplate = false,
  onClearAllFields,
}) => {
  // Local state for Field List enhancements (search + multi-select)
  const [fieldSearch, setFieldSearch] = React.useState('');
  const [selectedFieldIds, setSelectedFieldIds] = React.useState<string[]>(bulkFieldIds);

  useEffect(() => {
    if (bulkFieldIds.length === 0 && selectedFieldIds.length > 0) {
      setSelectedFieldIds([]);
    }
  }, [bulkFieldIds, selectedFieldIds.length]);

  useEffect(() => {
    onBulkFieldIdsChange?.(selectedFieldIds);
  }, [selectedFieldIds, onBulkFieldIdsChange]);

  const formatTargetIds = React.useMemo(() => {
    const ids =
      selectedFieldIds.length > 0
        ? selectedFieldIds
        : selectedField
          ? [selectedField.id]
          : [];
    return ids.filter((id) => {
      const f = fields.find((x) => x.id === id);
      return f && isTextBasedFieldType(f.type);
    });
  }, [selectedFieldIds, selectedField, fields]);

  // === A11Y: roving tabindex + focus restoration + live announcements ===
  const [rovingFieldId, setRovingFieldId] = React.useState<string | null>(null);
  const [rovingSnapId, setRovingSnapId] = React.useState<string | null>(null);
  const fieldItemRefs = React.useRef<Map<string, HTMLDivElement>>(new Map());
  const snapItemRefs = React.useRef<Map<string, HTMLDivElement>>(new Map());
  const signaturesTabRef = React.useRef<HTMLButtonElement>(null);
  const fieldTabRef = React.useRef<HTMLButtonElement>(null);
  const [liveMessage, setLiveMessage] = React.useState('');
  const afterDeleteFocusRef = React.useRef<string | null>(null); // id we want to focus after delete completes

  const announceTimeoutRef = React.useRef<number | null>(null);

  const announce = React.useCallback((msg: string) => {
    setLiveMessage(msg);
    if (announceTimeoutRef.current !== null) {
      clearTimeout(announceTimeoutRef.current);
    }
    // Clear after polite announcement has been read
    announceTimeoutRef.current = window.setTimeout(() => {
      announceTimeoutRef.current = null;
      setLiveMessage('');
    }, 1400);
  }, []);

  React.useEffect(() => {
    return () => {
      if (announceTimeoutRef.current !== null) {
        clearTimeout(announceTimeoutRef.current);
      }
    };
  }, []);

  // After fields change (post delete/bulk), attempt focus restoration to next logical item
  React.useEffect(() => {
    const targetId = afterDeleteFocusRef.current;
    if (!targetId) return;
    // If target still exists in current fields, select + focus it
    const stillThere = fields.some(f => f.id === targetId);
    if (stillThere) {
      onFieldSelect(targetId);
      setRovingFieldId(targetId);
      // focus the dom node (after paint)
      requestAnimationFrame(() => {
        const el = fieldItemRefs.current.get(targetId);
        el?.focus();
      });
    } else if (fields.length > 0) {
      // fallback: focus first remaining
      const first = fields[0].id;
      onFieldSelect(first);
      setRovingFieldId(first);
      requestAnimationFrame(() => fieldItemRefs.current.get(first)?.focus());
    } else {
      onFieldSelect(null);
      setRovingFieldId(null);
    }
    afterDeleteFocusRef.current = null;
  }, [fields, onFieldSelect]);

  // Filtered fields based on search
  const filteredFields = React.useMemo(() => {
    if (!fieldSearch.trim()) return fields;

    const q = fieldSearch.toLowerCase().trim();
    return fields.filter(f => {
      const haystack = [
        f.type,
        f.value || '',
        `p.${f.pageIndex + 1}`,
        f.type.includes('signature') ? 'signature' : '',
      ].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [fields, fieldSearch]);

  // Signature search (symmetric compact filter by label, only when >4 like fields pattern)


  // Lightweight groups + native drag reorder helpers (no new deps, session-only collapse state)
  const [collapsedGroups, setCollapsedGroups] = React.useState<Set<string>>(new Set());
  const [collapsedSnapGroups, setCollapsedSnapGroups] = React.useState<Set<string>>(new Set());

  // Inline editing states for snapshot rename + group (replaces all blocking prompt() dialogs for snapshots)
  const [editingSnapNameId, setEditingSnapNameId] = React.useState<string | null>(null);
  const [editingSnapNameValue, setEditingSnapNameValue] = React.useState('');
  const [editingSnapGroupId, setEditingSnapGroupId] = React.useState<string | null>(null);
  const [editingSnapGroupValue, setEditingSnapGroupValue] = React.useState('');

  // BUG-22: Inline editing states for field group assignment (replaces blocking prompt() calls)
  const [editingFieldGroupId, setEditingFieldGroupId] = React.useState<string | null>(null);
  const [editingFieldGroupValue, setEditingFieldGroupValue] = React.useState('');

  // Drag feedback state for fields (and snapshots) — ghost, insertion line, highlights (no new libs)
  const [draggingFieldId, setDraggingFieldId] = React.useState<string | null>(null);
  const [dragOverFieldId, setDragOverFieldId] = React.useState<string | null>(null);
  const [dragOverFieldPos, setDragOverFieldPos] = React.useState<'before' | 'after' | null>(null);
  const [draggingSnapId, setDraggingSnapId] = React.useState<string | null>(null);
  const [dragOverSnapId, setDragOverSnapId] = React.useState<string | null>(null);
  const [dragOverSnapPos, setDragOverSnapPos] = React.useState<'before' | 'after' | null>(null);

  const toggleGroup = (g: string) => setCollapsedGroups(prev => {
    const n = new Set(prev);
    if (n.has(g)) n.delete(g); else n.add(g);
    return n;
  });
  const toggleSnapGroup = (g: string) => setCollapsedSnapGroups(prev => {
    const n = new Set(prev);
    if (n.has(g)) n.delete(g); else n.add(g);
    return n;
  });

  /** Compute insert-before/after from pointer Y within a list row (shared by dragOver + drop). */
  const getDragInsertPosition = (
    e: React.DragEvent<HTMLElement>
  ): 'before' | 'after' => {
    const rect = e.currentTarget.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    return e.clientY < midY ? 'before' : 'after';
  };

  const setFieldGroup = (id: string, group: string | undefined) => {
    onFieldUpdate(id, { group: group || undefined });
  };

  // Commit/cancel helpers for snapshot inline rename + group (blur/Enter/Esc)
  const commitSnapRename = (id: string) => {
    const val = editingSnapNameValue.trim().slice(0, 100);
    if (val && onRenameSnapshot) {
      onRenameSnapshot(id, val);
      announce(`Snapshot renamed to ${val}`);
    }
    setEditingSnapNameId(null);
    setEditingSnapNameValue('');
  };
  const cancelSnapRename = () => {
    setEditingSnapNameId(null);
    setEditingSnapNameValue('');
  };
  const commitSnapGroup = (id: string) => {
    const val = editingSnapGroupValue.trim();
    if (onSetSnapshotGroup) onSetSnapshotGroup(id, val || undefined);
    setEditingSnapGroupId(null);
    setEditingSnapGroupValue('');
  };
  const cancelSnapGroup = () => {
    setEditingSnapGroupId(null);
    setEditingSnapGroupValue('');
  };

  // BUG-22: Inline field group commit/cancel helpers
  const commitFieldGroup = (id: string) => {
    const val = editingFieldGroupValue.trim();
    setFieldGroup(id, val || undefined);
    setEditingFieldGroupId(null);
    setEditingFieldGroupValue('');
  };
  const cancelFieldGroup = () => {
    setEditingFieldGroupId(null);
    setEditingFieldGroupValue('');
  };

  const fieldGroups = React.useMemo(() => {
    const byGroup: Record<string, SigningField[]> = {};
    const order: string[] = [];
    const seen = new Set<string>();
    // Stable group order from master list (first appearance)
    fields.forEach(f => {
      const g = (f.group || 'Ungrouped').trim() || 'Ungrouped';
      if (!seen.has(g)) { seen.add(g); order.push(g); }
      if (!byGroup[g]) byGroup[g] = [];
    });
    // Fill using filtered results (preserves search + relative order)
    filteredFields.forEach(f => {
      const g = (f.group || 'Ungrouped').trim() || 'Ungrouped';
      if (!byGroup[g]) byGroup[g] = [];
      byGroup[g].push(f);
    });
    const displayOrder = order.filter(g => (byGroup[g] || []).length > 0);
    return { order: displayOrder, byGroup };
  }, [fields, filteredFields]);

  const snapGroups = React.useMemo(() => {
    const byGroup: Record<string, Snapshot[]> = {};
    const order: string[] = [];
    const seen = new Set<string>();
    snapshots.forEach(s => {
      const g = (s.group || 'Ungrouped').trim() || 'Ungrouped';
      if (!seen.has(g)) { seen.add(g); order.push(g); }
      if (!byGroup[g]) byGroup[g] = [];
      byGroup[g].push(s);
    });
    return { order, byGroup };
  }, [snapshots]);

  // Flat ordered lists of *currently visible* items for roving arrow navigation (respects collapse + search filter)
  const flatFieldItems = React.useMemo(() => {
    const out: SigningField[] = [];
    fieldGroups.order.forEach(g => {
      if (!collapsedGroups.has(g)) {
        (fieldGroups.byGroup[g] || []).forEach(f => out.push(f));
      }
    });
    return out;
  }, [fieldGroups, collapsedGroups]);

  // F70: keep the roving cursor synced with the visible list. When the user
  // filters via search or collapses a group, flatFieldItems changes; if the
  // currently-roving id is no longer visible, fall back to the first visible
  // item (or null) so ArrowDown/Up resume from a sensible position.
  React.useEffect(() => {
    if (rovingFieldId && !flatFieldItems.some((f) => f.id === rovingFieldId)) {
      setRovingFieldId(flatFieldItems[0]?.id ?? null);
    }
  }, [flatFieldItems, rovingFieldId]);

  const flatSnapItems = React.useMemo(() => {
    const out: Snapshot[] = [];
    snapGroups.order.forEach(g => {
      if (!collapsedSnapGroups.has(g)) {
        (snapGroups.byGroup[g] || []).forEach(s => out.push(s));
      }
    });
    return out;
  }, [snapGroups, collapsedSnapGroups]);

  // Roving + arrow nav for Placed Fields list (arrow up/down moves selection + focus)
  const onFieldsListKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!flatFieldItems.length) return;
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;

    e.preventDefault();
    const currentId = rovingFieldId || selectedField?.id || flatFieldItems[0].id;
    const curIdx = flatFieldItems.findIndex(f => f.id === currentId);
    let nextIdx = curIdx;
    if (e.key === 'ArrowDown') nextIdx = Math.min(flatFieldItems.length - 1, curIdx < 0 ? 0 : curIdx + 1);
    if (e.key === 'ArrowUp') nextIdx = Math.max(0, curIdx < 0 ? 0 : curIdx - 1);

    const next = flatFieldItems[nextIdx];
    if (!next) return;

    onFieldSelect(next.id);
    setRovingFieldId(next.id);
    // focus the actual row for roving tabindex + visible focus ring
    requestAnimationFrame(() => {
      fieldItemRefs.current.get(next.id)?.focus();
    });
  };

  // Roving + arrow nav for Snapshots list
  const onSnapsListKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!flatSnapItems.length) return;
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;

    e.preventDefault();
    const currentId = rovingSnapId || comparingSnapshotId || flatSnapItems[0].id;
    const curIdx = flatSnapItems.findIndex(s => s.id === currentId);
    let nextIdx = curIdx;
    if (e.key === 'ArrowDown') nextIdx = Math.min(flatSnapItems.length - 1, curIdx < 0 ? 0 : curIdx + 1);
    if (e.key === 'ArrowUp') nextIdx = Math.max(0, curIdx < 0 ? 0 : curIdx - 1);

    const next = flatSnapItems[nextIdx];
    if (!next) return;

    setRovingSnapId(next.id);
    // For snaps we don't auto-load on arrows (safer); user presses Enter (existing) or clicks Compare
    // But we do move visual focus
    requestAnimationFrame(() => {
      snapItemRefs.current.get(next.id)?.focus();
    });
  };

  const [tab, setTab] = useState<'signatures' | 'field'>('signatures');

  // While any field is selected we force the "Field" properties tab (common for
  // inspectors). This makes "add text → click it → inspector shows value + format
  // controls" reliable. Manual switch to Signatures is only respected when nothing
  // is selected.
  const effectiveTab: 'signatures' | 'field' = selectedField ? 'field' : tab;
  const [signerExpanded, setSignerExpanded] = useState(() =>
    !!(signerProfile.name || signerProfile.company || signerProfile.title || signerProfile.email)
  );

  // Roving arrow-key navigation between the Signatures / Field tabs (ARIA tablist).
  // Left/Right (and Home/End) move the active tab and focus the newly selected tab.
  const onTabsKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    let nextTab: 'signatures' | 'field' | null = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') nextTab = 'field';
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') nextTab = 'signatures';
    else if (e.key === 'Home') nextTab = 'signatures';
    else if (e.key === 'End') nextTab = 'field';
    if (!nextTab || nextTab === tab) {
      if (nextTab) e.preventDefault();
      return;
    }
    e.preventDefault();
    setTab(nextTab);
    const target = nextTab === 'signatures' ? signaturesTabRef.current : fieldTabRef.current;
    requestAnimationFrame(() => target?.focus());
  };

  // Auto-switch to Field tab when a field is selected.
  // Fix A11Y-2: also sync rovingFieldId so the selected row gets tabIndex=0
  // when selection arrives from the canvas (not from list interaction).
  useEffect(() => {
    if (selectedField) {
      setTab('field');
      // Only sync when the roving id differs; avoids overwriting after a list click
      // that already called setRovingFieldId itself.
      setRovingFieldId((prev) => (prev === selectedField.id ? prev : selectedField.id));
    }
  }, [selectedField]);

  return (
    <aside className="editor-right-inspector">
      {hasPdf && (
        <div className="inspector-document-context px-2 pt-2 pb-1 border-b border-[var(--border-hairline)]">
          {workflowStage && (
            <div className="mb-2 flex items-center gap-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                Status
              </span>
              <span
                className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                  workflowStage === 'stale' || workflowStage === 'error'
                    ? 'bg-state-warning-bg text-state-warning'
                    : workflowStage === 'signed'
                      ? 'bg-state-success-bg text-state-success'
                      : 'bg-[var(--bg-stage)] text-[var(--text-secondary)]'
                }`}
              >
                {WORKFLOW_STAGE_LABELS[workflowStage]}
              </span>
            </div>
          )}
          <div className="rail-section !mt-0">
            <div className="rail-label">Document</div>
            <div className="rail-metric">
              <span>File</span>
              <strong className="truncate max-w-[140px]" title={documentName}>
                {documentName || 'Untitled'}
              </strong>
            </div>
            <div className="rail-metric"><span>Pages</span><strong>{pageCount}</strong></div>
            {fileSize > 0 && (
              <div className="rail-metric">
                <span>Size</span>
                <strong>{(fileSize / 1024 / 1024).toFixed(1)} MB</strong>
              </div>
            )}
            <div className="rail-metric"><span>Fields</span><strong>{fields.length}</strong></div>
            <div className="rail-metric"><span>Saved sigs</span><strong>{savedSignatureCount}</strong></div>
          </div>
          {hasAcroFormFields && (
            <div
              className="inspector-note"
              role="note"
              aria-label="Warning: PDF contains pre-existing form fields"
            >
              Contains existing form fields (AcroForm). Visual overlays only — existing fields are not filled or edited.
            </div>
          )}
          {memoryTip && (
            <div
              className="inspector-note"
              role="note"
              aria-label="Memory / complexity tip"
            >
              {memoryTip}
            </div>
          )}
          {onProfileChange && (
            <div className="rail-section mt-2">
              <button
                type="button"
                className="rail-label w-full text-left flex items-center gap-1 hover:text-accent dark:hover:text-accent transition-colors"
                onClick={() => setSignerExpanded((v) => !v)}
                aria-expanded={signerExpanded}
              >
                <span className="w-3 inline-block text-center" aria-hidden="true">
                  {signerExpanded ? '▾' : '▸'}
                </span>
                Signer profile
              </button>
              {signerExpanded && (
                <div className="mt-1 space-y-1">
                  <input
                    className="rail-input"
                    placeholder="Full name"
                    value={signerProfile.name}
                    onChange={(e) => onProfileChange({ name: e.target.value })}
                    aria-label="Signer full name"
                  />
                  <input
                    className="rail-input"
                    placeholder="Company"
                    value={signerProfile.company}
                    onChange={(e) => onProfileChange({ company: e.target.value })}
                    aria-label="Signer company"
                  />
                  <input
                    className="rail-input"
                    placeholder="Title"
                    value={signerProfile.title}
                    onChange={(e) => onProfileChange({ title: e.target.value })}
                    aria-label="Signer title"
                  />
                  <input
                    className="rail-input"
                    placeholder="Email"
                    type="email"
                    value={signerProfile.email}
                    onChange={(e) => onProfileChange({ email: e.target.value })}
                    aria-label="Signer email"
                  />
                  <p className="inspector-hint-text">
                    Used by Business preset and name/date fields. Stored locally only.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <div className="inspector-tabs" role="tablist" aria-label="Inspector sections" onKeyDown={onTabsKeyDown}>
        <button
          id="inspector-tab-signatures"
          role="tab"
          aria-selected={effectiveTab === 'signatures'}
          aria-controls="inspector-tabpanel-signatures"
          tabIndex={effectiveTab === 'signatures' ? 0 : -1}
          ref={signaturesTabRef}
          className={effectiveTab === 'signatures' ? 'active' : ''}
          onClick={() => setTab('signatures')}
        >
          Signatures
        </button>
        <button
          id="inspector-tab-field"
          role="tab"
          aria-selected={effectiveTab === 'field'}
          aria-controls="inspector-tabpanel-field"
          tabIndex={effectiveTab === 'field' ? 0 : -1}
          ref={fieldTabRef}
          className={effectiveTab === 'field' ? 'active' : ''}
          onClick={() => setTab('field')}
        >
          Field
        </button>
      </div>

      {effectiveTab === 'signatures' ? (
        <div
          className="inspector-tab-pane"
          key="signatures"
          id="inspector-tabpanel-signatures"
          role="tabpanel"
          aria-labelledby="inspector-tab-signatures"
          tabIndex={0}
        >
          <SignaturesPanel
            savedSignatures={savedSignatures}
            hasPdf={hasPdf}
            isBusy={isBusy}
            onCreateDraw={() => onCreateDrawSignature?.()}
            onCreateType={() => onCreateTypeSignature?.()}
            onCreateUpload={() => onCreateUploadSignature?.()}
            onPlace={onPlaceSavedSignature}
            onRename={onRenameSavedSignature}
            onDelete={onDeleteSavedSignature}
          />
        </div>
      ) : (
        <div
          className="inspector-tab-pane inspector-field-tab"
          key="field"
          id="inspector-tabpanel-field"
          role="tabpanel"
          aria-labelledby="inspector-tab-field"
          tabIndex={0}
        >
          <FieldPropertiesPanel
            field={selectedField}
            fields={fields}
            formatTargetIds={formatTargetIds}
            onUpdate={onFieldUpdate}
            onApplyTextFormat={onApplyTextFormat}
            onDelete={onFieldDelete}
            signerProfile={signerProfile}
          />

          {/* High-impact Field List with Search + Multi-select */}
          <div className="inspector-content border-t pt-3 mt-2">
            <div className="flex items-center justify-between mb-2 px-1 gap-2">
              <div className="inspector-section-label flex-shrink-0 flex items-center gap-1.5">
                Placed Fields <span className="font-mono">({fields.length})</span>
                {selectedFieldIds.length > 0 && (
                  <span className="ml-1 px-1.5 py-px rounded bg-state-warning-bg dark:bg-state-warning/60 text-state-warning dark:text-state-warning-bg text-[10px] font-medium tabular-nums" aria-live="polite">
                    {selectedFieldIds.length} selected
                  </span>
                )}
              </div>

              {/* Search input - compact and friendly */}
              {fields.length > 3 && (
                <input
                  type="text"
                  placeholder="Filter fields..."
                  value={fieldSearch}
                  onChange={(e) => setFieldSearch(e.target.value)}
                  className="flex-1 min-w-0 text-xs px-2 py-1 rounded border border-[var(--border-hairline)] bg-[var(--bg-panel)] focus:outline-none focus:border-accent"
                  aria-label="Filter placed fields"
                />
              )}

              {/* Select all / none links (only when >5 fields — small multi-select UX polish) */}
              {fields.length > 5 && (
                <div className="flex items-center gap-1 text-[10px] flex-shrink-0">
                  <button
                    onClick={() => setSelectedFieldIds(filteredFields.map(f => f.id))}
                    className="px-1.5 py-0.5 text-accent hover:text-accent dark:text-accent hover:underline focus:outline focus:outline-1 focus:outline-accent"
                    title="Select all fields matching current filter/search"
                  >
                    Select all visible
                  </button>
                  <span className="text-[var(--text-muted)] select-none">|</span>
                  <button
                    onClick={() => setSelectedFieldIds([])}
                    className="inspector-link-btn focus:outline focus:outline-1 focus:outline-accent"
                  >
                    None
                  </button>
                </div>
              )}

              {fields.length > 0 && onClearAllFields && (
                <button
                  onClick={() => {
                    if (confirm(`Delete all ${fields.length} fields? This cannot be undone.`)) {
                      announce(`${fields.length} fields deleted`);
                      afterDeleteFocusRef.current = null;
                      // BUG-11: single call = single undo entry (Contract #2)
                      onClearAllFields();
                    }
                  }}
                  className="text-[10px] px-2 py-0.5 rounded bg-state-danger-bg text-state-danger hover:bg-state-danger-bg dark:bg-state-danger/30 dark:text-state-danger font-medium transition-colors flex-shrink-0"
                >
                  Clear all
                </button>
              )}
            </div>

            {fields.length === 0 ? (
              <div className="text-xs text-[var(--text-muted)] py-3 px-1 italic" role="status">No fields placed on this document yet.</div>
            ) : (
              <>
                {/* Bulk actions when multi-select is active */}
                {selectedFieldIds.length > 0 && (
                  <div className="inspector-warning-banner">
                    <span className="font-medium text-state-warning dark:text-state-warning-bg">{selectedFieldIds.length} selected</span>
                    <button
                      onClick={() => {
                        if (confirm(`Delete ${selectedFieldIds.length} selected fields?`)) {
                          // Announce + prepare focus restore to a logical next item (first non-deleted after the min selected)
                          const toDelete = new Set(selectedFieldIds);
                          const survivors = flatFieldItems.filter(f => !toDelete.has(f.id));
                          const candidate = survivors[0]?.id || null;
                          if (candidate) afterDeleteFocusRef.current = candidate;
                          announce(`${selectedFieldIds.length} fields deleted`);
                          selectedFieldIds.forEach(id => onFieldDelete(id));
                          setSelectedFieldIds([]);
                        }
                      }}
                      className="px-2 py-0.5 rounded bg-state-danger-bg text-state-danger hover:bg-state-danger-bg text-[10px] font-medium"
                    >
                      Delete selected
                    </button>
                    <button
                      onClick={() => setSelectedFieldIds([])}
                      className="inspector-row-btn"
                    >
                      Cancel
                    </button>
                  </div>
                )}

                {/* Drag-to-reorder + grouped (folder) list — native HTML5, works with filter */}
                <div
                  className="inspector-list custom-scrollbar"
                  role="list"
                  aria-label="Placed fields list. Drag to reorder. Click or use keyboard to select. Arrow keys navigate."
                  onKeyDown={onFieldsListKeyDown}
                  tabIndex={-1}
                >
                  {fieldGroups.order.map((groupName) => {
                    const items = fieldGroups.byGroup[groupName] || [];
                    const isCollapsed = collapsedGroups.has(groupName);
                    return (
                      <div key={groupName}>
                        {/* Group/folder header (collapsible) */}
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => toggleGroup(groupName)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleGroup(groupName); } }}
                          className="inspector-group-header focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-accent"
                          title={`Click or press Enter to ${isCollapsed ? 'expand' : 'collapse'} group "${groupName}"`}
                          aria-expanded={!isCollapsed}
                          aria-label={`Group ${groupName}, ${items.length} fields, ${isCollapsed ? 'collapsed' : 'expanded'}`}
                        >
                          <span className="w-3 inline-block text-center" aria-hidden="true">{isCollapsed ? '▸' : '▾'}</span>
                          <span className="truncate">📁 {groupName}</span>
                          <span className="ml-auto text-[9px] font-normal opacity-60">({items.length})</span>
                        </div>

                        {!isCollapsed && items.map((field) => {
                          const isSel = field.id === selectedField?.id;
                          const isMultiSelected = selectedFieldIds.includes(field.id);
                          // Index shown is from full list for user reference (stable)
                          const fullIdx = fields.findIndex(f => f.id === field.id);

                          let label = '—';
                          if (field.value) {
                            label = field.value.length > 32 ? field.value.slice(0, 29) + '…' : field.value;
                          } else if (field.type.includes('signature')) {
                            label = 'Signature';
                          } else if (field.type === 'checkbox') {
                            label = field.isChecked ? 'Checked' : 'Unchecked';
                          }
                          const typeLabel = field.type.replace(/-/g, ' ');

                          // BUG-22: inline group badge — opens inline <input> instead of blocking prompt()
                          const groupBadge = field.group ? (
                            editingFieldGroupId === field.id ? (
                              <input
                                type="text"
                                value={editingFieldGroupValue}
                                onChange={(e) => setEditingFieldGroupValue(e.target.value)}
                                onBlur={() => commitFieldGroup(field.id)}
                                onKeyDown={(e) => {
                                  // Fix A11Y-1: stop Backspace/Delete from bubbling to
                                  // the ancestor field-row handler that deletes the field.
                                  e.stopPropagation();
                                  if (e.key === 'Enter') { e.preventDefault(); commitFieldGroup(field.id); }
                                  if (e.key === 'Escape') { e.preventDefault(); cancelFieldGroup(); }
                                }}
                                autoFocus
                                className="ml-1 w-20 text-[9px] px-1 py-0.5 rounded border border-state-warning bg-[var(--bg-panel)] focus:outline-none"
                                onClick={e => e.stopPropagation()}
                                placeholder="group"
                              />
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingFieldGroupId(field.id);
                                  setEditingFieldGroupValue(field.group || '');
                                }}
                                className="ml-1 text-[9px] px-1 py-0 rounded bg-accent-muted dark:bg-accent-soft text-accent dark:text-accent-muted hover:bg-accent-muted dark:hover:bg-accent-soft-strong"
                                title="Click to change group (inline)"
                              >
                                {field.group}
                              </button>
                            )
                          ) : null;

                          return (
                            <div
                              key={field.id}
                              data-inspector-field-id={field.id}
                              role="button"
                              tabIndex={rovingFieldId === field.id ? 0 : -1}
                              ref={(el) => {
                                if (el) fieldItemRefs.current.set(field.id, el);
                                else fieldItemRefs.current.delete(field.id);
                              }}
                              draggable
                              onDragStart={(e) => {
                                setDraggingFieldId(field.id);
                                e.dataTransfer.setData('text/plain', field.id);
                                e.dataTransfer.effectAllowed = 'move';
                              }}
                              onDragEnd={() => {
                                setDraggingFieldId(null);
                                setDragOverFieldId(null);
                                setDragOverFieldPos(null);
                              }}
                              onDragEnter={(e) => { e.preventDefault(); }}
                              onDragOver={(e) => {
                                e.preventDefault();
                                e.dataTransfer.dropEffect = 'move';
                                const pos = getDragInsertPosition(e);
                                if (dragOverFieldId !== field.id || dragOverFieldPos !== pos) {
                                  setDragOverFieldId(field.id);
                                  setDragOverFieldPos(pos);
                                }
                              }}
                              onDragLeave={(e) => {
                                const related = e.relatedTarget as Node | null;
                                if (!related || !e.currentTarget.contains(related)) {
                                  if (dragOverFieldId === field.id) {
                                    setDragOverFieldId(null);
                                    setDragOverFieldPos(null);
                                  }
                                }
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                const draggedId = e.dataTransfer.getData('text/plain');
                                const from = fields.findIndex(f => f.id === draggedId);
                                let to = fields.findIndex(f => f.id === field.id);
                                // Compute from the drop event — React state from the last dragOver can be stale/null on drop.
                                const pos = getDragInsertPosition(e);
                                if (pos === 'after') to += 1;
                                if (from !== -1 && from < to) to -= 1;
                                setDraggingFieldId(null);
                                setDragOverFieldId(null);
                                setDragOverFieldPos(null);
                                if (draggedId && draggedId !== field.id && from !== -1 && to !== -1 && from !== to && onReorderFields) {
                                  onReorderFields(from, to);
                                }
                              }}
                              onClick={(e) => {
                                // Multi-select with Shift or Ctrl/Cmd
                                if (e.shiftKey || e.ctrlKey || e.metaKey) {
                                  setSelectedFieldIds(prev =>
                                    prev.includes(field.id)
                                      ? prev.filter(id => id !== field.id)
                                      : [...prev, field.id]
                                  );
                                } else {
                                  onFieldSelect(field.id);
                                  setSelectedFieldIds([]);
                                  setRovingFieldId(field.id);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  onFieldSelect(field.id);
                                  setSelectedFieldIds([]);
                                  setRovingFieldId(field.id);
                                }
                                if ((e.key === 'Delete' || e.key === 'Backspace') && !e.metaKey && !e.ctrlKey) {
                                  e.preventDefault();
                                  // Focus restore: pick the next item in flat list after this one
                                  const curIdx = flatFieldItems.findIndex(f => f.id === field.id);
                                  const nextCandidate = flatFieldItems[curIdx + 1]?.id || flatFieldItems[curIdx - 1]?.id || null;
                                  afterDeleteFocusRef.current = nextCandidate;
                                  announce('Field deleted');
                                  onFieldDelete(field.id);
                                }
                                // Keyboard reorder (Alt+↑ / Alt+↓) on focused field item — calls onReorderFields (history recorded in hook)
                                if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown') && onReorderFields) {
                                  e.preventDefault();
                                  const currentIdx = fields.findIndex(f => f.id === field.id);
                                  if (e.key === 'ArrowUp' && currentIdx > 0) {
                                    onReorderFields(currentIdx, currentIdx - 1);
                                  } else if (e.key === 'ArrowDown' && currentIdx < fields.length - 1) {
                                    onReorderFields(currentIdx, currentIdx + 1);
                                  }
                                }
                              }}
                              className={`group relative flex items-center gap-1.5 px-2 py-1 cursor-pointer text-xs transition-colors border-l-2 border-transparent focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-accent ${
                                isSel 
                                  ? 'bg-accent-muted dark:bg-accent-soft text-accent dark:text-accent-muted border-l-accent' 
                                  : isMultiSelected
                                    ? 'bg-state-warning-bg dark:bg-state-warning/30'
                                    : 'inspector-row-hover'
                              } ${dragOverFieldId === field.id ? 'ring-1 ring-inset ring-accent bg-accent-soft dark:bg-accent-soft' : ''}`}
                              title="Drag to reorder • Alt+↑/↓ to keyboard reorder (when focused) • Click/Enter select • Del delete (Shift/Cmd multi)"
                              aria-label={`${typeLabel} field on page ${field.pageIndex + 1}: ${label || 'empty'}${field.group ? `, group ${field.group}` : ''}${isSel ? ', selected' : ''}`}
                              aria-pressed={isSel}
                            >
                              {/* Insertion indicator line (thin indigo) shown when dragging over this target */}
                              {draggingFieldId && dragOverFieldId === field.id && dragOverFieldPos === 'before' && (
                                <div className="absolute left-1 right-1 -top-px h-0.5 bg-accent rounded z-10" aria-hidden="true" />
                              )}

                              {/* Drag handle — more obvious grip (visual + hover indigo highlight) */}
                              <span
                                className="inspector-drag-handle group-hover:text-accent active:cursor-grabbing"
                                title="Drag to reorder"
                                aria-hidden="true"
                              >
                                ⋮⋮
                              </span>

                              {/* Multi-select checkbox */}
                              <input
                                type="checkbox"
                                checked={isMultiSelected}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setSelectedFieldIds(prev =>
                                    prev.includes(field.id)
                                      ? prev.filter(id => id !== field.id)
                                      : [...prev, field.id]
                                  );
                                }}
                                onClick={e => e.stopPropagation()}
                                className="accent-accent"
                              />

                              <span className="font-mono text-[10px] w-5 text-[var(--text-muted)] tabular-nums">{fullIdx + 1}</span>
                              <span className="font-semibold capitalize text-[var(--text-secondary)] min-w-[80px]">{typeLabel}</span>
                              <span className="text-[var(--text-chrome)] truncate flex-1">p.{field.pageIndex + 1} · {label}</span>

                              {groupBadge}

                              {/* Quick group assign (📁) — BUG-22: inline input instead of blocking prompt() */}
                              {editingFieldGroupId === field.id && !field.group ? (
                                <input
                                  type="text"
                                  value={editingFieldGroupValue}
                                  onChange={(e) => setEditingFieldGroupValue(e.target.value)}
                                  onBlur={() => commitFieldGroup(field.id)}
                                  onKeyDown={(e) => {
                                    // Fix A11Y-1: stop Backspace/Delete from bubbling to
                                    // the ancestor field-row handler that deletes the field.
                                    e.stopPropagation();
                                    if (e.key === 'Enter') { e.preventDefault(); commitFieldGroup(field.id); }
                                    if (e.key === 'Escape') { e.preventDefault(); cancelFieldGroup(); }
                                  }}
                                  autoFocus
                                  className="w-20 text-[9px] px-1 py-0.5 rounded border border-state-warning bg-[var(--bg-panel)] focus:outline-none"
                                  onClick={e => e.stopPropagation()}
                                  placeholder="group name"
                                />
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingFieldGroupId(field.id);
                                    setEditingFieldGroupValue(field.group || '');
                                  }}
                                  className="opacity-0 group-hover:opacity-100 text-state-warning hover:text-state-warning px-0.5"
                                  title="Assign to group / folder (inline)"
                                  aria-label={`Assign field on page ${field.pageIndex + 1} to a group`}
                                >
                                  📁
                                </button>
                              )}

                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const curIdx = flatFieldItems.findIndex(f => f.id === field.id);
                                  const nextCandidate = flatFieldItems[curIdx + 1]?.id || flatFieldItems[curIdx - 1]?.id || null;
                                  afterDeleteFocusRef.current = nextCandidate;
                                  announce('Field deleted');
                                  onFieldDelete(field.id);
                                }}
                                className="opacity-0 group-hover:opacity-100 text-state-danger hover:text-state-danger px-1.5 py-0.5 rounded hover:bg-state-danger-bg dark:hover:bg-state-danger/40 transition-opacity"
                                title="Delete this field"
                                aria-label={`Delete field on page ${field.pageIndex + 1}`}
                              >
                                ×
                              </button>

                              {/* Insertion indicator line (after) */}
                              {draggingFieldId && dragOverFieldId === field.id && dragOverFieldPos === 'after' && (
                                <div className="absolute left-1 right-1 -bottom-px h-0.5 bg-accent rounded z-10" aria-hidden="true" />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>

                {filteredFields.length === 0 && fieldSearch && (
                  <div className="text-xs text-[var(--text-muted)] py-2 px-1">No fields match “{fieldSearch}”</div>
                )}
              </>
            )}

            {/* Snapshots list - high-impact for before/after comparison. Now persisted + supports rename + shows per-document context. */}
            {(onExportTemplate || onImportTemplate) && (
              <div className="mt-3 pt-3 border-t px-1 flex flex-wrap gap-2">
                {onExportTemplate && (
                  <button
                    type="button"
                    disabled={!canExportTemplate}
                    onClick={onExportTemplate}
                    className="inspector-action-btn disabled:opacity-40"
                  >
                    Export template
                  </button>
                )}
                {onImportTemplate && (
                  <button
                    type="button"
                    onClick={onImportTemplate}
                    className="inspector-action-btn"
                  >
                    Import template
                  </button>
                )}
              </div>
            )}

            {snapshots && snapshots.length > 0 && (
              <div className="mt-3 pt-3 border-t">
                <div className="inspector-section-label mb-1.5 px-1 flex items-center justify-between">
                  <span>Snapshots ({snapshots.length})</span>
                  <span className="normal-case font-normal text-[10px] text-[var(--text-muted)]">persisted</span>
                </div>
                {/* Snapshots with drag-to-reorder + lightweight groups/folders */}
                <div
                  className="space-y-px"
                  role="list"
                  aria-label="Saved snapshots list for comparison. Arrow keys move focus."
                  onKeyDown={onSnapsListKeyDown}
                  tabIndex={-1}
                >
                  {snapGroups.order.map((groupName) => {
                    const items = snapGroups.byGroup[groupName] || [];
                    const isCollapsed = collapsedSnapGroups.has(groupName);
                    return (
                      <div key={groupName}>
                        {/* Snapshot group header */}
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => toggleSnapGroup(groupName)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSnapGroup(groupName); } }}
                          className="inspector-group-header mt-1 rounded-t focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-accent"
                          title={`Click or press Enter to ${isCollapsed ? 'expand' : 'collapse'} snapshot group "${groupName}"`}
                          aria-expanded={!isCollapsed}
                          aria-label={`Snapshot group ${groupName}, ${items.length} snapshots, ${isCollapsed ? 'collapsed' : 'expanded'}`}
                        >
                          <span className="w-3 inline-block text-center" aria-hidden="true">{isCollapsed ? '▸' : '▾'}</span>
                          <span className="truncate">📁 {groupName}</span>
                          <span className="ml-auto text-[9px] font-normal opacity-60">({items.length})</span>
                        </div>

                        {!isCollapsed && items.map((snap) => {
                          const isComparing = snap.id === comparingSnapshotId;
                          return (
                            <div
                              key={snap.id}
                              role="button"
                              tabIndex={rovingSnapId === snap.id ? 0 : -1}
                              ref={(el) => {
                                if (el) snapItemRefs.current.set(snap.id, el);
                                else snapItemRefs.current.delete(snap.id);
                              }}
                              draggable
                              onDragStart={(e) => {
                                setDraggingSnapId(snap.id);
                                e.dataTransfer.setData('text/plain', snap.id);
                                e.dataTransfer.effectAllowed = 'move';
                              }}
                              onDragEnd={() => {
                                setDraggingSnapId(null);
                                setDragOverSnapId(null);
                                setDragOverSnapPos(null);
                              }}
                              onDragEnter={(e) => { e.preventDefault(); }}
                              onDragOver={(e) => {
                                e.preventDefault();
                                e.dataTransfer.dropEffect = 'move';
                                const pos = getDragInsertPosition(e);
                                if (dragOverSnapId !== snap.id || dragOverSnapPos !== pos) {
                                  setDragOverSnapId(snap.id);
                                  setDragOverSnapPos(pos);
                                }
                              }}
                              onDragLeave={(e) => {
                                const related = e.relatedTarget as Node | null;
                                if (!related || !e.currentTarget.contains(related)) {
                                  if (dragOverSnapId === snap.id) {
                                    setDragOverSnapId(null);
                                    setDragOverSnapPos(null);
                                  }
                                }
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                const draggedId = e.dataTransfer.getData('text/plain');
                                const from = snapshots.findIndex(s => s.id === draggedId);
                                let to = snapshots.findIndex(s => s.id === snap.id);
                                const pos = getDragInsertPosition(e);
                                if (pos === 'after') to += 1;
                                if (from !== -1 && from < to) to -= 1;
                                setDraggingSnapId(null);
                                setDragOverSnapId(null);
                                setDragOverSnapPos(null);
                                if (draggedId && draggedId !== snap.id && from !== -1 && to !== -1 && from !== to && onReorderSnapshots) {
                                  onReorderSnapshots(from, to);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  onLoadSnapshot?.(snap);
                                }
                                if (e.key.toLowerCase() === 'c') {
                                  e.preventDefault();
                                  onCompareSnapshot?.(snap.id);
                                }
                              }}
                              className={`flex items-center justify-between group relative text-xs px-2 py-1 transition-colors border-l-2 border-transparent focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-accent ${
                                isComparing 
                                  ? 'bg-accent-muted dark:bg-accent-soft ring-1 ring-inset ring-accent-muted dark:ring-accent' 
                                  : 'inspector-row-hover'
                              } ${dragOverSnapId === snap.id ? 'ring-1 ring-inset ring-accent bg-accent-soft dark:bg-accent-soft' : ''}`}
                              title="Drag to reorder • Enter to load • C key to compare"
                              aria-label={`Snapshot: ${snap.name} with ${snap.fields.length} fields${snap.documentName ? ` from ${snap.documentName}` : ''}${isComparing ? ', currently comparing' : ''}`}
                            >
                              {/* Snapshot insertion line (before) */}
                              {draggingSnapId && dragOverSnapId === snap.id && dragOverSnapPos === 'before' && (
                                <div className="absolute left-1 right-1 -top-px h-0.5 bg-accent rounded z-10" aria-hidden="true" />
                              )}
                              {editingSnapNameId === snap.id ? (
                                <input
                                  type="text"
                                  value={editingSnapNameValue}
                                  onChange={(e) => setEditingSnapNameValue(e.target.value)}
                                  onBlur={() => commitSnapRename(snap.id)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') { e.preventDefault(); commitSnapRename(snap.id); }
                                    if (e.key === 'Escape') { e.preventDefault(); cancelSnapRename(); }
                                  }}
                                  autoFocus
                                  className="flex-1 min-w-0 text-sm px-1.5 py-0.5 rounded border border-state-warning bg-[var(--bg-panel)] focus:outline-none focus:border-state-warning"
                                  onClick={e => e.stopPropagation()}
                                />
                              ) : (
                                <button
                                  onClick={() => onLoadSnapshot?.(snap)}
                                  className="flex-1 min-w-0 text-left truncate font-medium text-[var(--text-secondary)] hover:text-accent dark:hover:text-accent"
                                  title={`Load snapshot: ${snap.name}${snap.documentName ? ` (from ${snap.documentName})` : ''}`}
                                >
                                  {snap.name}
                                  <span className="text-[10px] text-[var(--text-muted)] ml-1">({snap.fields.length} fields)</span>
                                  {snap.documentName && (
                                    <span
                                      className="inspector-snapshot-source"
                                      title={`Saved while viewing: ${snap.documentName}`}
                                    >
                                      · {snap.documentName.length > 18 ? snap.documentName.slice(0, 15) + '…' : snap.documentName}
                                    </span>
                                  )}
                                </button>
                              )}

                              <button
                                onClick={() => onCompareSnapshot?.(snap.id)}
                                className={`text-[10px] px-1.5 py-0.5 rounded flex-shrink-0 transition-colors ${
                                  isComparing 
                                    ? 'bg-accent text-white hover:bg-accent dark:bg-accent dark:hover:bg-accent' 
                                    : 'bg-accent-muted text-accent hover:bg-accent-muted dark:bg-accent-soft dark:text-accent'
                                }`}
                                title={isComparing ? "Exit visual compare mode (removes ghost overlay)" : "Compare this snapshot visually (shows as ghost layer)"}
                                aria-label={isComparing ? "Exit visual compare for this snapshot" : "Compare this snapshot visually as ghost layer"}
                              >
                                {isComparing ? 'Exit Compare' : 'Compare'}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingSnapNameId(snap.id);
                                  setEditingSnapNameValue(snap.name);
                                  // ensure group edit closed
                                  setEditingSnapGroupId(null);
                                }}
                                className="opacity-0 group-hover:opacity-100 text-state-warning hover:text-state-warning px-1 flex-shrink-0"
                                title="Rename snapshot (inline)"
                                aria-label="Rename this snapshot inline"
                              >
                                ✎
                              </button>

                              {/* Group control for snapshots — now inline edit instead of prompt */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingSnapGroupId(snap.id);
                                  setEditingSnapGroupValue(snap.group || '');
                                  setEditingSnapNameId(null);
                                }}
                                className="opacity-0 group-hover:opacity-100 text-state-warning hover:text-state-warning px-0.5 flex-shrink-0"
                                title="Assign / edit group (inline)"
                                aria-label="Edit snapshot group inline"
                              >
                                📁
                              </button>

                              {editingSnapGroupId === snap.id ? (
                                <input
                                  type="text"
                                  value={editingSnapGroupValue}
                                  onChange={(e) => setEditingSnapGroupValue(e.target.value)}
                                  onBlur={() => commitSnapGroup(snap.id)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') { e.preventDefault(); commitSnapGroup(snap.id); }
                                    if (e.key === 'Escape') { e.preventDefault(); cancelSnapGroup(); }
                                  }}
                                  autoFocus
                                  className="w-24 text-[10px] px-1 py-0.5 rounded border border-state-warning bg-[var(--bg-panel)] focus:outline-none"
                                  onClick={e => e.stopPropagation()}
                                  placeholder="group"
                                />
                              ) : snap.group ? (
                                <button
                                  onClick={(e) => { e.stopPropagation(); onSetSnapshotGroup?.(snap.id, undefined); }}
                                  className="opacity-0 group-hover:opacity-100 inspector-row-btn px-0.5"
                                  title="Remove from group (click to ungroup)"
                                  aria-label="Remove from group"
                                >
                                  {snap.group}
                                </button>
                              ) : null}

                              <button
                                onClick={() => {
                                  announce('Snapshot deleted');
                                  onDeleteSnapshot?.(snap.id);
                                }}
                                className="opacity-0 group-hover:opacity-100 text-state-danger hover:text-state-danger px-1 flex-shrink-0"
                                title="Delete snapshot"
                                aria-label="Delete snapshot"
                              >
                                ×
                              </button>

                              {/* Snapshot insertion line (after) */}
                              {draggingSnapId && dragOverSnapId === snap.id && dragOverSnapPos === 'after' && (
                                <div className="absolute left-1 right-1 -bottom-px h-0.5 bg-accent rounded z-10" aria-hidden="true" />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    {/* aria-live polite region for action feedback (e.g. "3 fields deleted", "Snapshot renamed") — screen readers only */}
    <div
      aria-live="polite"
      className="absolute -left-[9999px] w-px h-px overflow-hidden whitespace-nowrap border-0 p-0 m-[-1px]"
      role="status"
    >
      {liveMessage}
    </div>
    </aside>
  );
};

/**
 * [A11] Render-cost fix: SigningWorkspace re-renders on every drag-move rAF commit
 * (new `fields[]` identity, plus zoom/pan/status state), which previously forced
 * RightInspector to re-render and fully reconcile its grouped Placed-Fields list in
 * parallel with the canvas drag. Wrapping in React.memo with the comparator below
 * lets the inspector bail out of renders triggered by incidental parent state
 * (zoom/pan/status) and by drags of fields it does not itself display.
 *
 * The call site passes ~20 inline-arrow handlers whose identity changes every parent
 * render, so a default shallow comparison would never bail out. Function props are
 * only ever invoked (their identity does not affect this component's output), so the
 * comparator deliberately ignores handler identity and instead compares:
 *   - the data props that drive rendering (fields, selectedField, savedSignatures,
 *     signerProfile, snapshots, bulkFieldIds),
 *   - the scalar/context props shown in the UI,
 *   - the *presence* (truthy/falsy) of the optional handlers that gate conditional
 *     rendering (onProfileChange, onClearAllFields, onExportTemplate, onImportTemplate,
 *     onReorderFields, onReorderSnapshots, onSetSnapshotGroup, onLoadSnapshot,
 *     onDeleteSnapshot, onCompareSnapshot, onRenameSnapshot, the signature-create
 *     handlers) — not their identity.
 *
 * Note: during an active drag of a SELECTED field the inspector still re-renders
 * because `selectedField` identity changes — that is acceptable and intended; the win
 * is eliminating renders on incidental parent state and on drags of non-selected fields.
 */
const arePropsEqual = (prev: RightInspectorProps, next: RightInspectorProps): boolean => {
  // Data props whose identity meaningfully drives this component's render.
  if (prev.fields !== next.fields) return false;
  if (prev.selectedField !== next.selectedField) return false;
  if (prev.savedSignatures !== next.savedSignatures) return false;
  if (prev.signerProfile !== next.signerProfile) return false;
  if (prev.snapshots !== next.snapshots) return false;
  if (prev.bulkFieldIds !== next.bulkFieldIds) return false;

  // Scalar / context props rendered in the UI.
  if (prev.documentName !== next.documentName) return false;
  if (prev.pageCount !== next.pageCount) return false;
  if (prev.fileSize !== next.fileSize) return false;
  if (prev.savedSignatureCount !== next.savedSignatureCount) return false;
  if (prev.hasAcroFormFields !== next.hasAcroFormFields) return false;
  if (prev.memoryTip !== next.memoryTip) return false;
  if (prev.workflowStage !== next.workflowStage) return false;
  if (prev.hasPdf !== next.hasPdf) return false;
  if (prev.isBusy !== next.isBusy) return false;
  if (prev.comparingSnapshotId !== next.comparingSnapshotId) return false;
  if (prev.canExportTemplate !== next.canExportTemplate) return false;

  // Optional handlers that gate conditional rendering: compare presence, not identity.
  const presence = (a?: unknown, b?: unknown) => Boolean(a) === Boolean(b);
  if (!presence(prev.onProfileChange, next.onProfileChange)) return false;
  if (!presence(prev.onClearAllFields, next.onClearAllFields)) return false;
  if (!presence(prev.onExportTemplate, next.onExportTemplate)) return false;
  if (!presence(prev.onImportTemplate, next.onImportTemplate)) return false;
  if (!presence(prev.onReorderFields, next.onReorderFields)) return false;
  if (!presence(prev.onReorderSnapshots, next.onReorderSnapshots)) return false;
  if (!presence(prev.onSetSnapshotGroup, next.onSetSnapshotGroup)) return false;
  if (!presence(prev.onLoadSnapshot, next.onLoadSnapshot)) return false;
  if (!presence(prev.onDeleteSnapshot, next.onDeleteSnapshot)) return false;
  if (!presence(prev.onCompareSnapshot, next.onCompareSnapshot)) return false;
  if (!presence(prev.onRenameSnapshot, next.onRenameSnapshot)) return false;
  if (!presence(prev.onCreateDrawSignature, next.onCreateDrawSignature)) return false;
  if (!presence(prev.onCreateTypeSignature, next.onCreateTypeSignature)) return false;
  if (!presence(prev.onCreateUploadSignature, next.onCreateUploadSignature)) return false;

  // Remaining handlers (onFieldUpdate, onApplyTextFormat, onFieldDelete, onFieldSelect,
  // onPlaceSavedSignature, onRenameSavedSignature, onDeleteSavedSignature,
  // onBulkFieldIdsChange) are always invoked and never gate rendering by identity, so
  // changes to their identity alone do not require a re-render.
  return true;
};

export const RightInspector = React.memo(RightInspectorComponent, arePropsEqual);
